import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  EmojiEvents as TrophyIcon,
  TrendingDown as DownIcon,
  LocationCity as DistrictIcon,
  Speed as AvgIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, DistrictBenchmarkData } from '../../services/learningAnalyticsApi';

const METRIC_LABELS: Record<string, string> = {
  student_teacher_ratio: 'STR',
  gpi: 'Gender Parity',
  teacher_attendance_rate: 'Teacher Att.',
  infrastructure_score: 'Infrastructure',
  student_attendance_rate: 'Student Att.',
  enrollment_growth: 'Enrollment Growth',
};

const DistrictBenchmarkingTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DistrictBenchmarkData | null>(null);
  const [year, setYear] = useState<string>('');
  const [level, setLevel] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (level) params.level = parseInt(level);
      const response = await learningAnalyticsAPI.getDistrictBenchmarking(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load benchmarking data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, level]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  if (!data) return null;

  const { summary, districts, national_averages, metrics } = data;

  // Build radar chart data for selected district vs national average
  const selectedDist = districts.find(d => d.district_name === selectedDistrict) || districts[0];
  const radarData = metrics.map(m => {
    const distVal = selectedDist?.[m] || 0;
    const natVal = national_averages[m] || 0;
    // Normalize to percentile for radar
    const percentile = selectedDist?.[`${m}_percentile`] || 50;
    return {
      metric: METRIC_LABELS[m] || m,
      district: percentile,
      average: 50, // National average is always at 50th percentile
    };
  });

  // Bar chart: enrollment by district
  const enrollmentData = districts.map(d => ({
    name: d.district_name,
    enrollment: d.total_enrollment,
    teachers: d.total_teachers,
  })).sort((a, b) => b.enrollment - a.enrollment);

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Year" value={year}
              onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="">Current</MenuItem>
              {[2026, 2025, 2024, 2023].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Education Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Compare District" value={selectedDistrict || (districts[0]?.district_name || '')}
              onChange={(e) => setSelectedDistrict(e.target.value)}>
              {districts.map(d => (
                <MenuItem key={d.district_name} value={d.district_name}>
                  #{d.rank} - {d.district_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DistrictIcon color="primary" />
                <Typography variant="body2" color="textSecondary">Total Districts</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{summary.total_districts}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrophyIcon sx={{ color: '#ffc107' }} />
                <Typography variant="body2" color="textSecondary">Top District</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {summary.top_district || '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DownIcon color="error" />
                <Typography variant="body2" color="textSecondary">Bottom District</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="error.main">
                {summary.bottom_district || '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AvgIcon color="info" />
                <Typography variant="body2" color="textSecondary">National Avg STR</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {national_averages.student_teacher_ratio || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Radar Chart */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {selectedDist?.district_name || 'District'} vs National Average
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Percentile comparison across 6 metrics
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  <Radar name={selectedDist?.district_name || 'District'} dataKey="district"
                    stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
                  <Radar name="National Avg" dataKey="average"
                    stroke="#f57c00" fill="#f57c00" fillOpacity={0.1} strokeDasharray="5 5" />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Enrollment Bar Chart */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Enrollment by District</Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={enrollmentData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip formatter={(value: number) => value.toLocaleString()} />
                  <Legend />
                  <Bar dataKey="enrollment" fill="#1976d2" name="Enrollment" />
                  <Bar dataKey="teachers" fill="#388e3c" name="Teachers" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* District Rankings Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>District Rankings</Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell align="right">Schools</TableCell>
                  <TableCell align="right">Enrollment</TableCell>
                  <TableCell align="right">Teachers</TableCell>
                  <TableCell align="right">STR</TableCell>
                  <TableCell align="right">GPI</TableCell>
                  <TableCell align="right">Teacher Att. %</TableCell>
                  <TableCell align="right">Infra Score</TableCell>
                  <TableCell align="right">Growth %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {districts.map((d) => (
                  <TableRow key={d.district_name} hover
                    selected={d.district_name === (selectedDistrict || districts[0]?.district_name)}
                    onClick={() => setSelectedDistrict(d.district_name)}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Chip
                        label={`#${d.rank}`}
                        size="small"
                        color={d.rank <= 3 ? 'success' : d.rank >= districts.length - 2 ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">{d.district_name}</Typography>
                    </TableCell>
                    <TableCell align="right">{d.school_count}</TableCell>
                    <TableCell align="right">{d.total_enrollment.toLocaleString()}</TableCell>
                    <TableCell align="right">{d.total_teachers.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip label={d.student_teacher_ratio} size="small"
                        color={d.student_teacher_ratio > 40 ? 'error' : d.student_teacher_ratio > 25 ? 'warning' : 'success'}
                        variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={d.gpi} size="small"
                        color={Math.abs(d.gpi - 1) < 0.03 ? 'success' : 'warning'}
                        variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{d.teacher_attendance_rate || '-'}%</TableCell>
                    <TableCell align="right">{d.infrastructure_score || '-'}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={(d.enrollment_growth || 0) > 0 ? 'success.main' : 'error.main'}>
                        {(d.enrollment_growth || 0) > 0 ? '+' : ''}{d.enrollment_growth || 0}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DistrictBenchmarkingTab;
